import {getRefServiceStatusSeverity, refServiceStatusSeverity} from "@ots-frontend/ots-data";
import React from "react";
import {isEqual} from "lodash";
import {getRefServiceStatusColor} from "../../../../util/ref-service-status-ui-util";
import {Clusterer, ClusterIconStyle, MarkerExtended, TCalculator} from "@react-google-maps/marker-clusterer";
import {getClusterMarkerOpts, MarkerClusterer, MarkerClustererProps} from "../MarkerClusterer/MarkerClusterer";
import {OpsStatusMarker, OpsStatusMarkerProps} from "../OpsStatusMarker/OpsStatusMarker";
import {OpsLocationStatus} from "../../../../model/ops-status";

export type OpsStatusMarkerClustererProps =
  Omit<MarkerClustererProps, "clusterer" | "children" | "averageCenter" | "enableRetinaIcons" | "gridSize" | "calculator" | "styles">
  & {
  locs: OpsLocationStatus[],
  onHover?: (e: google.maps.MapMouseEvent, ctx: OpsLocationStatus) => void;
  onHoverOut?: (e: google.maps.MapMouseEvent) => void;
  onMarkerClick?: (e: google.maps.MapMouseEvent, ctx: OpsLocationStatus) => void;
}

const getWorstStatusForCluster = (markers: MarkerExtended[]): string => {
  let worst: string | null = null;

  for (const marker of markers) {
    const loc = marker.get("opsStatus") as OpsLocationStatus | undefined;
    if (loc) {
      const status = loc.overallStatus;
      if (!worst) {
        worst = status;
      } else if (getRefServiceStatusSeverity(status) <= getRefServiceStatusSeverity(worst)) {
        worst = status;
      }
    }
  }

  return worst ?? "UNKNOWN";
}

export const OpsStatusMarkerClusterer = (props: OpsStatusMarkerClustererProps) => {
  const {
    locs,
    onHover,
    onHoverOut,
    onMarkerClick,
    ...rest
  } = props;

  const clusterCalculator: TCalculator = React.useCallback((markers: MarkerExtended[], _numStyles: number) => {
    const worstStatus = getWorstStatusForCluster(markers);
    return {
      text: String(markers.length),
      index: Object.keys(CLUSTER_ICONS).indexOf(worstStatus) ?? 3,
      title: `${markers.length} locations`,
    };
  }, []);

  return (
    <MarkerClusterer
      {...rest}
      enableRetinaIcons={true}
      gridSize={60}
      calculator={clusterCalculator}
      styles={CLUSTER_STYLES}
    >
      {(clusterer) =>
        <>
          {
            locs.map((e, index) => {
              // TODO: review for memo
              const opts = getClusterMarkerOpts(e.siteId, index, locs.length, clusterer);

              return (
                <MarkerInCluster
                  key={opts.key}
                  noClustererRedraw={opts.noClustererRedraw}
                  ctx={e}
                  onHover={onHover}
                  onHoverOut={onHoverOut}
                  onMarkerClick={onMarkerClick}
                  clusterer={clusterer}
                  index={index}
                  totalCount={locs.length}/>
              );
            })
          }
        </>
      }
    </MarkerClusterer>
  );
}

type MarkerInClusterProps =
  Omit<OpsStatusMarkerProps, "position" | "clusterer">
  & {
  clusterer?: Clusterer,
  index: number
  totalCount: number
}

const MarkerInCluster = (props: MarkerInClusterProps) => {
  const {
    clusterer,
    index,
    totalCount,
    ctx,
    ...rest
  } = props;

  return (
    <OpsStatusMarker
      {...rest}
      clusterer={clusterer}
      ctx={ctx}
    />
  );
};

export const OpsStatusMarkerClustererMemo = React.memo(
  OpsStatusMarkerClusterer,
  isEqual
);

const makeClusterSvg = (color: string, size = 48): string => {
  const radius = size / 2 - 2;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${radius}"
              fill="${color}"
              stroke="white"
              stroke-width="4" />
    </svg>
  `;

  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}

const makeStatusClusterIcons = () => {
  const icons: Record<string, string> = {};

  Object.keys(refServiceStatusSeverity)
    .forEach((status) => {
      icons[status] = makeClusterSvg(getRefServiceStatusColor(status));
    });

  return icons;
}

const CLUSTER_ICONS = makeStatusClusterIcons();

const CLUSTER_STYLES : ClusterIconStyle[] = Object.values(CLUSTER_ICONS).map((url) => ({ url, width: 48, height: 48, textColor: "#fff", textSize: 14 }))
